import { AnnouncementsModule } from '@b2b/common/components/announcements/announcements.component';
import {
  CartDirectiveModule,
  ContentHeaderDirectiveModule,
  HeaderDirectiveModule,
  NotesDirectiveModule,
  OverlayStatusDirectiveModule,
  SidebarDirectiveModule,
} from '@b2b/common/directives';
import { NavigationDirectiveModule } from '@b2b/common/directives/navigation/navigation.directive';
import { AppsService } from '@b2b/common/services/apps/apps.service';
import { ThemesDataResolver, UserConfigDataResolver } from '@b2b/common/resolvers';
import uiRouter, { StateProvider, StateService } from '@uirouter/angularjs';
import agGrid from 'ag-grid';
import angular from 'angular';
import { filter, firstValueFrom } from 'rxjs';
import { ApplicationControllerModule } from './controllers/ApplicationController';
import { HybridDemoControllerModule } from './hybrid-demo/hybrid-demo.controller';
import {
  AccountView,
  CardholdersView,
  CartView,
  CouponsView,
  CustomersView,
  DashboardView,
  DonationsView,
  EntitiesView,
  FinanceView,
  FormsView,
  GroupsView,
  ImportExportView,
  InventoryView,
  LayoutsView,
  MembershipView,
  OrdersView,
  PartnersView,
  PassesView,
  AppStoreView,
  PermissionsView,
  PosView,
  PricingView,
  ProgramsView,
  ReportsView,
  ResourcesView,
  RetailView,
  RolesView,
  SchedulerView,
  SettingsView,
  SettlementView,
  SherpaView,
  SubscriptionsView,
  TemplatesView,
  ThemesView,
  UsersView,
} from './views';

/**
 * The Application module is the main entry point for the application
 *
 * Other views are located in the `src/app/views` directory and are loaded
 * into the context of application.default as subviews of the "main" ui-view
 */
agGrid.initialiseAgGridWithAngular1(angular);

export const ApplicationView = angular
  .module('acme.view.application', [
    uiRouter,
    'ngAnimate',
    'acme.common.directive.permit',
    HeaderDirectiveModule,
    OverlayStatusDirectiveModule,
    'acme.common.directive.footer',
    'acme.common.directive.search',
    AnnouncementsModule,
    NavigationDirectiveModule,
    'acme.common.directive.navExpanded',
    'acme.common.directive.navFocus',
    'acme.common.directive.screenReaderNavSelected',
    'acme.common.directive.viewMenu',
    SidebarDirectiveModule,
    ContentHeaderDirectiveModule,
    'acme.common.directive.contentSubheader',
    'acme.common.directive.perfectScrollbar',
    'acme.common.directive.history',
    'acme.common.directive.auditTrail',
    NotesDirectiveModule,
    'acme.common.directive.capitalize',
    'acme.common.directive.dateConverter',
    'acme.common.directive.kbDraggable',
    'acme.common.directive.summernote',
    'acme.common.directive.modalTitle',
    'acme.common.directive.printerFriendlyLink',
    'acme.common.directive.emailResources',
    CartDirectiveModule,
    DashboardView,
    OrdersView,
    CustomersView,
    FormsView,
    ProgramsView,
    RetailView,
    TemplatesView,
    ResourcesView,
    MembershipView,
    AccountView,
    EntitiesView,
    UsersView,
    InventoryView,
    ImportExportView,
    DonationsView,
    PartnersView,
    PricingView,
    CouponsView,
    ReportsView,
    FinanceView,
    ThemesView,
    SchedulerView,
    SettlementView,
    SettingsView,
    GroupsView,
    PermissionsView,
    RolesView,
    SherpaView,
    SubscriptionsView,
    PosView,
    LayoutsView,
    PassesView,
    CartView,
    CardholdersView,
    AppStoreView,
    ApplicationControllerModule,
    HybridDemoControllerModule,
    'acme.common.service.api',
    'acme.common.service.auth',
    'acme.view.pageNotFound.controllers',
    'acme.view.resolveFailed.controllers',
    'acme.view.downtime.controllers',
  ])

  /**
   * Each section or module of the site can also have its own routes. AngularJS
   * will handle ensuring they are all available at run-time, but splitting it
   * this way makes each module more "self-contained".
   *
   * Routes will be added into the application by the $stateProvider in individual views
   */
  .config(function config($stateProvider: StateProvider) {
    const application = {
      name: 'application',
      url: '/app',
      abstract: true,
      views: {
        main: {
          controller: 'ApplicationController',
          templateUrl: 'layouts/application.tpl.html',
        },
      },
      data: {
        pageTitle: 'Home',
        pageClass: 'application',
        roles: ['admin', 'user'],
      },
      resolve: {
        UserConfigData: UserConfigDataResolver(),
        ThemesData: ThemesDataResolver({ includeEmail: true }),
      },
    };

    const applicationDefault = {
      name: 'application.default',
      url: '',
      views: {
        'subview-main': {
          templateUrl: 'views/application/application.tpl.html',
        },
      },
    };

    const applicationFailedResolve = {
      name: 'application.failed',
      url: '/error',
      views: {
        'subview-main': {
          controller: 'ResolveFailedController',
          templateUrl: 'common/views/resolveFailed/resolveFailed.tpl.html',
        },
      },
    };

    const applicationPageNotFound = {
      name: 'application.notfound',
      url: '/notfound',
      views: {
        'subview-main': {
          controller: 'PageNotFoundController',
          templateUrl: 'common/views/pageNotFound/pageNotFound.tpl.html',
        },
      },
    };

    const applicationDowntime = {
      name: 'application.downtime',
      url: '/downtime',
      views: {
        'subview-main': {
          controller: 'DowntimeController',
          templateUrl: 'common/views/downtime/downtime.tpl.html',
        },
      },
    };

    // HYBRID: Example Angular route - demonstrates Angular component in AngularJS state
    // The <ng-example> component is a downgraded Angular component registered in
    // ui/src/ng/app/upgrade/ng-downgraded-components.ts
    // The actual Angular component is in ui/src/ng/app/features/example/
    const applicationNgExample = {
      name: 'application.ng-example',
      url: '/ng-example',
      views: {
        'subview-main': {
          template: '<ng-example></ng-example>',
        },
      },
      data: {
        pageTitle: 'Angular Example',
        pageClass: 'application',
      },
    };

    // DEV-13071: Gift Cards product config settings page. The <ng-gift-cards>
    // component is a downgraded Angular 18 component registered in
    // apps/b2b-frontend/app/upgrade/ng-downgraded-components.ts. Modeled on
    // applicationNgExample above (a real Angular component mounted into the
    // 'subview-main' named view of the authenticated app shell), not on the
    // standalone zendesk-login splash state. Reached from the "Gift Cards"
    // nav item added imperatively by ApplicationController (DEV-13600) once
    // the stored_value_activate extendable resolves.
    const applicationGiftCards = {
      name: 'application.giftCards',
      url: '/gift-cards',
      views: {
        'subview-main': {
          template: '<ng-gift-cards></ng-gift-cards>',
        },
      },
      data: {
        pageTitle: 'Gift Cards',
        pageClass: 'application',
      },
      onEnter: ['$state', 'AppsService', function($state: StateService, appsService: AppsService) {
        appsService.ensureLoaded();
        return firstValueFrom(
          appsService.state$.pipe(filter((state) => state.status !== 'loading')),
        ).then((state) => {
          if (state.status !== 'loaded' || !appsService.hasStoredValueActivateApp()) {
            return $state.go('application.default');
          }
        });
      }],
    };

    // HYBRID: Demo view showing all three frameworks together
    // - AngularJS: template + controller
    // - Angular 18: downgraded components (ng-alert)
    // - Lit: web components (example-card)
    const applicationHybridDemo = {
      name: 'application.hybrid-demo',
      url: '/hybrid-demo',
      views: {
        'subview-main': {
          templateUrl: 'views/application/hybrid-demo/hybrid-demo.tpl.html',
          controller: 'HybridDemoController',
          controllerAs: '$ctrl',
        },
      },
      data: {
        pageTitle: 'Hybrid Demo',
        pageClass: 'application',
      },
    };

    $stateProvider
      .state(application)
      .state(applicationDefault)
      .state(applicationFailedResolve)
      .state(applicationPageNotFound)
      .state(applicationDowntime)
      .state(applicationNgExample)
      .state(applicationHybridDemo)
      .state(applicationGiftCards);
  }).name;
